import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  customerName: string;
  customerPhone: string;
}

const NAV_ITEMS = [
  { icon: 'ri-shopping-bag-3-line', label: 'My Orders', tab: 'orders' },
  { icon: 'ri-map-pin-2-line',      label: 'Track Order', tab: 'tracking' },
  { icon: 'ri-wallet-3-line',       label: 'My Wallet', tab: 'wallet' },
  { icon: 'ri-coupon-line',         label: 'Coupons', tab: 'coupons' },
];

export default function SideDrawer({ open, onClose, customerName, customerPhone }: SideDrawerProps) {
  const location = useLocation();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 flex flex-col transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-orange-500 to-red-500 px-5 pt-10 pb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" onClick={onClose} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <i className="ri-t-shirt-line text-white text-base"></i>
              </div>
              <span className="text-white font-bold text-base">ClothyBazar</span>
            </Link>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* User info */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <i className="ri-user-fill text-white text-xl"></i>
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm truncate">{customerName || 'Guest User'}</p>
              <p className="text-white/70 text-xs truncate">{customerPhone || 'Place an order to get started'}</p>
            </div>
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest px-3 mb-2">My Account</p>
          {NAV_ITEMS.map(item => {
            const isActive = location.pathname === '/profile';
            return (
              <Link
                key={item.tab}
                to={`/profile?tab=${item.tab}`}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl mb-1 transition-all cursor-pointer ${
                  isActive ? 'text-gray-700 hover:bg-orange-50 hover:text-orange-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-gray-600 text-base`}></i>
                </div>
                <span className="font-medium text-sm">{item.label}</span>
                <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
              </Link>
            );
          })}

          <div className="my-3 border-t border-gray-100"></div>
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest px-3 mb-2">Store</p>

          <Link
            to="/"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-3 rounded-xl mb-1 text-gray-600 hover:bg-gray-50 transition-all cursor-pointer"
          >
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
              <i className="ri-store-2-line text-gray-600 text-base"></i>
            </div>
            <span className="font-medium text-sm">Shop Now</span>
            <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
          </Link>

          <Link
            to="/product-listing"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-3 rounded-xl mb-1 text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all cursor-pointer"
          >
            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center shrink-0">
              <i className="ri-add-box-line text-orange-500 text-base"></i>
            </div>
            <span className="font-medium text-sm">List a Product</span>
            <span className="ml-auto text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-bold">NEW</span>
          </Link>
        </nav>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100">
          <p className="text-gray-400 text-xs text-center">ClothyBazar &copy; {new Date().getFullYear()}</p>
        </div>
      </div>
    </>
  );
}
